import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useDebounceValue } from '../../../../../hooks/useDebounceValue';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../../models';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getIndustries, getIndustryName, isTemplateNew } from '../../../../../models/UiUtils';
import {
	useAdminAccountQuery,
	useEmailCategoryTemplates,
	useEmailSearchTemplates,
	useMyTemplatesFilterInfiniteQuery,
	useTemplateIndustryCategoriesQuery,
} from '../../../../../queries';
import {
	ITemplate,
	ITemplateCard,
	ImpersonationBroker,
	Industry,
	TemplateType,
} from '../../../../../viewmodels/AppViewModels';
import { Dialog, DialogPopup, DialogTitle } from '../../../../components-v2/Dialog';
import { InputGroupAddon, InputGroupButton, InputGroupInput, InputGroupV2 } from '../../../../components-v2/InputGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { TabToggle } from '../../../../components-v2/TabToggle';
import { filterLevDraftTemplates } from '../../../../containers/Campaigns/utils';
import { emailActivityTintColor } from '../../../../styles/colors';
import { bs } from '../../../../styles/styles';
import { CampaignExpiryHint } from '../../../campaigns/CampaignExpiryHint';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../HoverCard';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { OopsNotFoundGraphic } from '../../../svgs/graphics/OopsNotFoundGraphic';
import { SearchIcon2 } from '../../../svgs/icons/SearchIcon2';
import { SocialMediaIndicatorIcon } from '../../../svgs/icons/SocialMediaIndicatorIcon';
import {
	CampaignSummaryFromContentShort,
	CampaignSummaryFromPreview,
} from '../../../templateCards/CampaignTemplateCard/presentation';
import { getTemplateApprovalStatus } from '../../../TemplateReviewStatusIndicator';
import { styleSheet } from './styles';
import { XIconNew } from '@WebComponents/svgs/icons/XIcon';
import { Button } from '@HoudiniUI/Button';

const MINE = 'mine';
const LIBRARY = 'library';
type BrowserScope = typeof MINE | typeof LIBRARY;

interface Props extends IImpersonationContextComponentProps {
	onTemplateSelected(template: ITemplate): void;
}

function _EmailTemplateBrowser(props: Props) {
	const { impersonationContext } = props;
	const id = React.useId();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('CampaignSearch');
	const account = impersonationContext?.isValid ? impersonationContext.account : userSession?.account;
	const accountQuery = useAdminAccountQuery({
		accountId: account?.id,
		enabled: !!account?.id,
	});
	// The impersonation context carries a Partial<IAccount> that often omits additionalInfo, and nothing re-reads it,
	// so seeding from it stranded impersonated accounts on Insurance. The admin query returns the full account.
	const accountIndustry =
		accountQuery.data?.additionalInfo?.industry ?? account?.additionalInfo?.industry ?? Industry.Insurance;
	const [selectedIndustry, setSelectedIndustry] = React.useState<Industry | null>(null);
	const industry = selectedIndustry ?? accountIndustry;
	// my template enabled either when not impersonating or if csmViewMyCampaignsEnabled when impersonating
	const isMyTemplatesEnabled =
		impersonationContext == null || accountQuery.data?.preferences?.csmViewMyCampaignsEnabled;

	// Both defaults depend on data that is still loading on first render, so they resolve at read time rather than
	// seeding state: seeding captured the pre-load value and never corrected once the queries settled.
	const [selectedScope, setSelectedScope] = React.useState<BrowserScope | null>(null);
	const scope: BrowserScope = selectedScope ?? (isMyTemplatesEnabled ? MINE : LIBRARY);
	// Until the impersonated account's preference lands there is no answer yet, and defaulting to the library would
	// fetch a scope the user is about to be moved off of.
	const isScopeResolved = impersonationContext == null || accountQuery.isFetched || !account?.id;
	const [selectedCategory, setSelectedCategory] = React.useState('');
	const [searchInput, setSearchInput] = React.useState('');
	const searchQuery = useDebounceValue(searchInput.trim(), 300);

	const templateIndustryCategoriesQuery = useTemplateIndustryCategoriesQuery({
		industry,
		impersonationContext,
	});
	const categories = React.useMemo(
		() => (templateIndustryCategoriesQuery.data ?? []).filter(category => category !== ''),
		[templateIndustryCategoriesQuery.data]
	);
	const category = selectedCategory || categories[0] || '';

	const myTemplatesQuery = useMyTemplatesFilterInfiniteQuery({
		enabled: isScopeResolved && scope === MINE,
		impersonationContext,
		// The route segment only gates the internal Levitate industry; the My Templates query itself is account-scoped
		// and industry-agnostic, so browsing the library elsewhere must not re-key this one.
		industry: accountIndustry,
		sort: 'desc',
		sortBy: 'creationDate',
		templateType: TemplateType.Email,
		term: searchQuery,
	});
	const librarySearchQuery = useEmailSearchTemplates({
		enabled: isScopeResolved && scope === LIBRARY && Boolean(searchQuery),
		impersonationContext,
		industry,
		query: searchQuery,
	});
	const libraryCategoryQuery = useEmailCategoryTemplates({
		categoryName: category,
		enabled: isScopeResolved && scope === LIBRARY && !searchQuery && Boolean(category),
		impersonationContext,
		industry,
	});

	const activeQuery = scope === MINE ? myTemplatesQuery : searchQuery ? librarySearchQuery : libraryCategoryQuery;
	const templates = React.useMemo(() => {
		let value: ITemplateCard[] = [];
		if (scope === MINE) {
			value = myTemplatesQuery.data?.pages?.flatMap(page => page.values ?? []) ?? [];
		} else if (searchQuery) {
			value = librarySearchQuery.data ?? [];
		} else {
			value = libraryCategoryQuery.data ?? [];
		}
		return filterLevDraftTemplates(value, impersonationContext?.isValid);
	}, [
		scope,
		searchQuery,
		myTemplatesQuery.data,
		librarySearchQuery.data,
		libraryCategoryQuery.data,
		impersonationContext?.isValid,
	]);

	// Only the first load blanks the list; a refetch keeps the previous results on screen so typing does not flash.
	const isInitialLoad = activeQuery.isPending || (scope === LIBRARY && !searchQuery && !category);
	const industryOptions = getIndustries({
		accountId: impersonationContext?.account?.id ?? userSession?.account?.id,
		brokerDealer:
			impersonationContext?.account?.additionalInfo?.brokerDealer ?? userSession?.account?.additionalInfo?.brokerDealer,
	}).map(i => ({
		label: getIndustryName(i),
		value: i,
	}));
	const scopeItems = [
		...(isMyTemplatesEnabled ? [{ label: 'My Templates', value: MINE }] : []),
		{ label: 'Library', value: LIBRARY },
	];

	// The search term deliberately survives a scope change: the empty state offers a jump to the library that only
	// makes sense if the term carries over.
	const onScopeChange = (value: string) => {
		setSelectedScope(value as BrowserScope);
	};
	const onIndustryClicked = (option: Industry) => {
		setSelectedIndustry(option);
		setSelectedCategory('');
	};
	const onCategoryClicked = (option: string) => {
		setSelectedCategory(option);
	};
	const onCampaignTemplateCardClicked = async (card: ITemplateCard, socialIndicator?: boolean) => {
		const templateId = socialIndicator
			? card.associatedTemplates?.find(x => x.templateType === TemplateType.SocialMediaPost)?.id
			: card.id;
		if (templateId) {
			try {
				const template = await userSession.webServiceHelper.callAsync<ITemplate>(
					ImpersonationBroker.composeApiUrl({
						impersonationContext,
						urlPath: `template/${encodeURIComponent(templateId)}`,
						queryParams: {
							expand: 'LastUsedByDate',
						},
					}),
					'GET'
				);
				if (template) {
					props.onTemplateSelected(template);
				}
			} catch (error) {
				errorMessages.pushApiError(error);
				logger.logApiError('TemplateLoad-Error', error);
			}
		}
	};
	const onSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInput(event.target.value);
	};
	const onClearSearch = () => {
		setSearchInput('');
	};

	return (
		<div className={`${css(styleSheet.container)}`}>
			<InputGroupV2 styles={[styleSheet.searchGroup]}>
				<InputGroupAddon align='inlineStart'>
					<SearchIcon2 height={16} width={16} />
				</InputGroupAddon>
				<InputGroupInput
					id={`${id}-template-search`}
					onChange={onSearchInputChange}
					placeholder='Search by name or keyword'
					type='text'
					value={searchInput}
				/>
				{searchInput ? (
					<InputGroupAddon align='inlineEnd'>
						<InputGroupButton aria-label='Clear search' size='iconXs' onClick={onClearSearch}>
							<XIconNew />
						</InputGroupButton>
					</InputGroupAddon>
				) : null}
			</InputGroupV2>
			{scopeItems.length > 1 ? (
				<TabToggle items={scopeItems} onValueChange={onScopeChange} styles={[styleSheet.scopeToggle]} value={scope} />
			) : null}
			{scope === LIBRARY ? (
				<>
					<Select items={industryOptions} value={industry} onValueChange={onIndustryClicked}>
						<SelectTrigger styles={styleSheet.trigger}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{industryOptions.map(i => (
								<SelectItem key={`${id}-${i.value}`} value={i.value}>
									{i.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{searchQuery ? (
						<div
							className={css(styleSheet.scopeHint)}
						>{`Search covers all ${getIndustryName(industry)} categories.`}</div>
					) : (
						<Select value={category} onValueChange={onCategoryClicked}>
							<SelectTrigger styles={styleSheet.trigger}>
								<SelectValue placeholder='All categories' />
							</SelectTrigger>
							<SelectContent>
								{categories.map(categoryOption => (
									<SelectItem key={`campaign-search-category-option-${categoryOption}`} value={categoryOption}>
										{categoryOption}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				</>
			) : null}
			{isInitialLoad ? <LoadingSpinner className={css(bs.absoluteCenter)} type='large' /> : null}
			{!isInitialLoad ? (
				<div className={css(styleSheet.cardsContainer)}>
					{templates.length === 0 && !myTemplatesQuery.hasNextPage ? (
						<div className={css(styleSheet.emptyState)}>
							<figure className={css(styleSheet.emptyStateFigure)}>
								<OopsNotFoundGraphic />
							</figure>
							<p className={css(styleSheet.emptyStateText)}>
								{searchQuery
									? scope === MINE
										? `No templates of yours match “${searchQuery}”.`
										: `No ${getIndustryName(industry)} campaigns match “${searchQuery}”.`
									: 'There are no templates here yet.'}
							</p>
							{searchQuery && scope === MINE ? (
								<Button variant='secondary' size='sm' onClick={() => setSelectedScope(LIBRARY)}>
									Search the library
								</Button>
							) : null}
						</div>
					) : null}
					{templates.map(templateCard => (
						<div
							key={templateCard.id}
							className={css(styleSheet.templateCard)}
							onClick={() => onCampaignTemplateCardClicked(templateCard)}
						>
							{getTemplateApprovalStatus(templateCard?.templateReview, false)}
							<div className={css(styleSheet.templateCardHeader)}>
								<div className={css(styleSheet.templateCardHeaderTitle)}>
									<span>{templateCard.name}</span>
									{templateCard?.schedule?.expirationDate ? (
										<CampaignExpiryHint expirationDate={moment(templateCard.schedule.expirationDate)} />
									) : null}
									{isTemplateNew(templateCard) ? <span style={{ color: emailActivityTintColor }}>(New!)</span> : null}
								</div>
								{!impersonationContext?.isValid &&
								userSession?.account?.features?.socialMedia?.enabled &&
								templateCard?.associatedTemplates?.length > 0 ? (
									<HoverCard>
										<HoverCardTrigger className={css(styleSheet.templateSocialIndicator)}>
											<SocialMediaIndicatorIcon className={css(bs.cursorPointer)} />
										</HoverCardTrigger>
										<HoverCardContent styles={[styleSheet.templateSocialPopover]}>
											Also available for social media
										</HoverCardContent>
									</HoverCard>
								) : null}
							</div>
							{templateCard.summary ? (
								<CampaignSummaryFromContentShort card={templateCard} />
							) : (
								<CampaignSummaryFromPreview card={templateCard} />
							)}

							<div className={css(styleSheet.templateCardButtonContainer)}>
								<Button
									variant='secondary'
									size='sm'
									onClick={(e: React.MouseEvent) => {
										e.stopPropagation();
										onCampaignTemplateCardClicked(templateCard);
									}}
									aria-label={`Select template ${templateCard.name}`}
								>
									Select Template
								</Button>
							</div>
						</div>
					))}
					{myTemplatesQuery.hasNextPage && !myTemplatesQuery.isFetchingNextPage ? (
						<Waypoint bottomOffset='-200px' onEnter={() => myTemplatesQuery.fetchNextPage()} />
					) : null}
					{myTemplatesQuery.isFetchingNextPage ? <LoadingSpinner type='small' /> : null}
				</div>
			) : null}
		</div>
	);
}

const EmailTemplateBrowserAsObserver = observer(_EmailTemplateBrowser);
const EmailTemplateBrowser = inject(ImpersonationContextKey)(EmailTemplateBrowserAsObserver);

interface EmailTemplateBrowserDialogProps {
	isOpen: boolean;
	onOpenChange(open: boolean): void;
	onTemplateSelected(template: ITemplate): void;
	title?: React.ReactNode;
}

export function EmailTemplateBrowserDialog({
	isOpen,
	onOpenChange,
	onTemplateSelected,
	title,
}: EmailTemplateBrowserDialogProps) {
	const handleTemplateSelected = (template: ITemplate) => {
		onTemplateSelected(template);
		onOpenChange(false);
	};
	return (
		<Dialog open={isOpen} onOpenChange={onOpenChange} dismissOnOutsideClick={false}>
			<DialogPopup styles={[styleSheet.popup]}>
				<DialogTitle styles={[styleSheet.title]}>
					{title || 'Create a new message template or pick from an existing one...'}
				</DialogTitle>
				<EmailTemplateBrowser onTemplateSelected={handleTemplateSelected} />
			</DialogPopup>
		</Dialog>
	);
}
